import { endSession } from '@/utils/session'
import store from '@/store'

// Backend endpoint. Same base as the axios instance in `@/api/request`.
const BASE_API = import.meta.env.VITE_APP_BASE_API || '/cfes/fodhandler/admintool'
const SUPPORT_BOT_URL = `${BASE_API}/support/bot/ask`

/**
 * Parse a single SSE frame (already split by "\n\n") into { event, data, raw }.
 * `data` is JSON-parsed when possible, otherwise returned as the raw string.
 * Blank frames or comment-only frames return null.
 */
const parseSseFrame = (frame) => {
  const lines = frame.split(/\r?\n/)
  let event = 'message'
  const dataLines = []
  for (const line of lines) {
    if (!line || line.startsWith(':')) continue // blank line / SSE comment
    const idx = line.indexOf(':')
    const field = idx === -1 ? line : line.slice(0, idx)
    // Per SSE spec, a single leading space after the colon is stripped.
    const value = idx === -1 ? '' : line.slice(idx + 1).replace(/^ /, '')
    if (field === 'event') event = value
    else if (field === 'data') dataLines.push(value)
    // `id` and `retry` fields are ignored — we don't use reconnection.
  }
  if (dataLines.length === 0 && event === 'message') return null
  const raw = dataLines.join('\n')
  let data = raw
  if (raw) {
    try { data = JSON.parse(raw) } catch { /* keep raw string */ }
  }
  return { event, data, raw }
}

/**
 * Handle 401 / 403 the same way `@/api/request` does for regular XHR calls.
 */
const handleAuthFailure = (status) => {
  console.warn(`[supportBot] SSO auth failed (HTTP ${status}), redirecting to login`)
  endSession({ toastMessage: 'Authentication expired. Redirecting to login...' })
}

/**
 * Ask the CFES support bot and stream the SSE response.
 *
 * Backend contract (Spring SseEmitter):
 *   POST /support/bot/ask   body: { question, sessionId? }   Accept: text/event-stream
 *   Context headers: X-Brand, X-Region (current selection or configured defaults).
 *   Events:
 *     - event: heartbeat  data: "ping"        → keep-alive, ignore
 *     - event: answer     data: BedrockSupportAnswerResponse JSON
 *     - event: done       data: { status: 'ok' }
 *     - event: error      data: { code, message }
 *
 * @param {object}   opts
 * @param {string}   opts.question
 * @param {string=}  opts.sessionId
 * @param {AbortSignal=} opts.signal   Optional signal to cancel the stream.
 * @param {(evt: {event: string, data: any, raw: string}) => void} opts.onEvent
 * @returns {Promise<void>} Resolves when the stream ends (done or server closes).
 */
export async function supportBotAskStream({ question, sessionId, signal, onEvent } = {}) {
  const body = { question }
  if (sessionId) body.sessionId = sessionId

  const resp = await fetch(SUPPORT_BOT_URL, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json;charset=utf-8',
      Accept: 'text/event-stream',
      'X-Brand': store.getters.brand || store.getters.defaultBrand,
      'X-Region': store.getters.region || store.getters.defaultRegion,
    },
    body: JSON.stringify(body),
    signal,
  })

  if (resp.status === 401 || resp.status === 403) {
    handleAuthFailure(resp.status)
    const err = new Error('Session expired')
    err.status = resp.status
    throw err
  }

  if (!resp.ok) {
    let text = ''
    try { text = await resp.text() } catch { /* ignore */ }
    const err = new Error(text || `HTTP ${resp.status}`)
    err.status = resp.status
    throw err
  }

  if (!resp.body || typeof resp.body.getReader !== 'function') {
    throw new Error('Streaming is not supported in this environment.')
  }

  const reader = resp.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''

  const dispatch = (frame) => {
    const evt = parseSseFrame(frame)
    if (!evt) return
    try {
      onEvent?.(evt)
    } catch (e) {
      console.error('[supportBot] onEvent handler threw', e)
    }
  }

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      let idx
      // Split on the SSE frame terminator (double newline, tolerating \r\n).
      while ((idx = buffer.search(/\r?\n\r?\n/)) >= 0) {
        const match = buffer.slice(idx).match(/^\r?\n\r?\n/)
        const sepLen = match ? match[0].length : 2
        const frame = buffer.slice(0, idx)
        buffer = buffer.slice(idx + sepLen)
        dispatch(frame)
      }
    }
    // Flush any trailing data that didn't end with the separator.
    const tail = buffer.trim()
    if (tail) dispatch(tail)
  } finally {
    try { reader.releaseLock() } catch { /* ignore */ }
  }
}
